import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

import { Currency } from '@app/core/models/customer.models';
import { ChartFrequency, ChartView } from '@app/shared/modules/charts/chart.models';
import { TableLayout } from '@app/shared/modules/table-layout/models/table-layout.model';
import { CustomerType } from '../enums/customer.enums';
import { AdPlatformType, CampaignOptimizationValue, FilterOption } from './common.models';
import { AllMetrics } from './metrics.constants';

export type UserRole = 'AGENCY-ADMIN' | 'CUSTOMER-ADMIN' | 'SYS-ADMIN' | 'USER';

export const UserRoles: UserRole[] = ['SYS-ADMIN', 'AGENCY-ADMIN', 'CUSTOMER-ADMIN', 'USER'];

export const UserAccessLevel: UserRole[] = ['USER', 'CUSTOMER-ADMIN', 'AGENCY-ADMIN', 'SYS-ADMIN'];

export type UserLanguage = 'DE' | 'EN' | 'ZH';

export enum UserSetting {
  General = 'general',
  PgGoalsUseUserCurrency = 'pgGoalsUseUserCurrency',
  TableLayout = 'tableLayout',
  HistoricChart = 'historicChart',
  LineChart = 'metricChart',
  MonitorKpiMetrics = 'MonitorKpiMetrics',
  InsightsKpiMetrics = 'InsightsKpiMetrics',
  GoalsDefaultSort = 'goalsDefaultSort',
  BudgetChart = 'budgetChart',
  AdGroupCampaignType = 'adGroupCampaignType',
}

export interface User {
  userId: number;
  intercomUserHash: number;
  email: string;
  emailVerified: boolean;
  firstName: string;
  lastName: string;
  // @deprecated
  role?: UserRole;
  lang: UserLanguage;
  sysAdmin: boolean;
  active?: boolean;
  isAuth0User?: boolean;
}

export interface SignupUser {
  signupId: string;
  type: 'CUSTOMER' | 'ACCOUNT';
  platform: AdPlatformType | null;
  amzRegion: string | null;
  origin?: 'MIRAKL';
  nextStep: string;
  failure?: string;
  termsConditionsAccepted?: boolean;
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  phone: string;
  lang: UserLanguage;
}

export interface UserCustomerAccess {
  customerId: number;
  customerName: string;
  customerType: CustomerType;
  mainCustomerId?: number;
  role: UserRole;
  notifications: boolean;
}

export interface UserGeneralSettings {
  pgGoalsIncludePausedCampaignsInMetrics?: boolean;
  currency?: Currency;
  coa?: boolean;
  accountInformationCheck?: AccountInformationCheckSettings;
}

export interface UserTableLayoutSettings {
  keyFigures: TableLayout;
  products: TableLayout;
  product: TableLayout;
}

export interface UserHistoricChartSettings {
  goalContext: AllMetrics;
}

export interface UserGoalsDefaultSortSettings {
  key: string;
  isAsc: boolean;
}

export interface UserLineChartSettings {
  frequency?: ChartFrequency;
  metrics?: AllMetrics[];
  viewMode?: ChartView;
}

export interface UserKpiOptimizationTypeMetricSettings {
  metric: AllMetrics;
  visible: boolean;
}

export interface UserKpiOptimizationTypeSettings {
  type: CampaignOptimizationValue;
  metrics: UserKpiOptimizationTypeMetricSettings[];
}

export interface UserKpiOptimizationSettings {
  kpi: UserKpiOptimizationTypeSettings[];
}

export interface AdGroupCampaignTypeSettings {
  visible?: boolean;
}

export interface AccountInformationCheckSettings {
  lastShown?: number;
  shownCount?: number;
}

export interface UserSettings {
  [UserSetting.General]: UserGeneralSettings;
  [UserSetting.TableLayout]: UserTableLayoutSettings;
  [UserSetting.HistoricChart]: UserHistoricChartSettings;
  [UserSetting.GoalsDefaultSort]: UserGoalsDefaultSortSettings;
  [UserSetting.LineChart]: UserLineChartSettings;
  [UserSetting.MonitorKpiMetrics]: UserKpiOptimizationSettings;
  [UserSetting.PgGoalsUseUserCurrency]: Record<number, Record<number, boolean>>;
  [UserSetting.InsightsKpiMetrics]: UserKpiOptimizationSettings;
  [UserSetting.BudgetChart]: UserLineChartSettings;
  [UserSetting.AdGroupCampaignType]: AdGroupCampaignTypeSettings;
}

export const TABLE_USER_ROLE_FILTER = (tl: TranslateService, isAgency?: boolean): Observable<FilterOption[]> => {
  return tl.get('common.roles').pipe(
    map((roles) =>
      UserRoles.filter((role) => role !== 'SYS-ADMIN' && (role !== 'AGENCY-ADMIN' || isAgency)).map((role) => ({
        text: roles[role],
        value: role,
      })),
    ),
  );
};
